import React, { useEffect, useState } from 'react';
import { deletePrescriptionFile, fetchFilesForPatient, uploadFile } from '../services/api';
import { getClinicIdFromToken, getValueFromToken } from '../utils/tokenUtils';
import { IconButton } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { FaTrash } from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { showToast } from '../utils/toastUtils';
import { downloadFile, handleResponseErrorData } from '../utils/commonUtils';
import ConfirmBox from "../components/ConfirmBox";
import DeleteIcon from "@mui/icons-material/Delete";

 pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

 const FileManager = ({ patientId, appointmentId }) => {
  const [files, setFiles] = useState([]);
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const clinicId = getClinicIdFromToken();

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await fetchFilesForPatient(patientId, clinicId);
        if (response?.status) {
          setFiles(response?.data);
        }
      } catch (error) {
        console.error('Error fetching files:', error);
      }
    };

    fetchFiles();
  }, [patientId, clinicId]);

  const handleUpload = async (acceptedFiles) => {
    try {
      const file = acceptedFiles[0];
      const fileType = file.type;

      if (fileType !== 'application/pdf' && fileType !== 'image/jpeg' && fileType !== 'image/png' && fileType !== 'image/jpg') {
        showToast('Only PDF, JPEG, JPG, and PNG files are allowed.', 'error');
        return;
      }

      const fileData = new FormData();
      fileData.append('file', acceptedFiles[0]);
      fileData.append('patient_id', patientId);
      fileData.append('appointment_id', appointmentId);
      fileData.append('file_type', acceptedFiles[0].type);
      fileData.append('created_by', getValueFromToken('user_id'));

      const response = await uploadFile(fileData);
      if (response?.status) {
        setFiles((prevFiles) => [...prevFiles, response.data]);
      }else{
        showToast(response?.message, 'error');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      handleResponseErrorData(error, null);
    }
  };

  const handleDelete = async (patientId, fileId) => {
    try {
      const response = await deletePrescriptionFile(patientId, fileId);
      if (response?.status) {
        showToast(response?.message, 'success');
        setFiles((prevFiles) => prevFiles.filter((file) => file.file_id !== fileId));
      }else{
        showToast(response?.message, 'error');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      handleResponseErrorData(error, null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: handleUpload });

  const sortedFiles = files.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="file-manager">
      <div
        {...getRootProps()}
        className={`dropzone border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center ${
          isDragActive ? 'border-blue-500' : 'border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p className="text-blue-500">Drop the files here...</p>
        ) : (
          <p className="text-gray-500">Drag and drop files here, or click to select files</p>
        )}
      </div>
      <div className="file-list grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {sortedFiles.map((file, index) => (
          <div
            key={file.file_id}
            className="file-item bg-white shadow-md rounded-lg p-4 flex items-center space-x-4 hover:shadow-lg transition duration-300 ease-in-out relative"
            onMouseEnter={() => setSelectedFileIndex(index)}
            onMouseLeave={() => setSelectedFileIndex(null)}
          >
          <div className="file-preview w-24 h-32 flex items-center justify-center">
            {file.file_type.includes('pdf') && (
              <Document file={file.url} loading={<div>Loading...</div>} error={<div>Failed to load.</div>}>
                <Page pageNumber={1} width={100} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
            )}
            {file.file_type.includes('image') && (
              <img src={file.url} alt="file" style={{ maxWidth: '100%', height: 'auto' }} />
            )}
          </div>
          <div className="file-details flex-grow">
            <p className="file-name text-gray-800 font-semibold">{file.url.split('/').pop()}</p>
            <p className="file-date text-gray-500 text-sm">{new Date(file.created_at).toLocaleDateString()}</p>
          </div>
          <div className="file-actions flex space-x-2">
              <IconButton aria-label="view" onClick={() => window.open(file.url, '_blank')}>
                <VisibilityOutlinedIcon />
              </IconButton>
              <IconButton
                aria-label="download"
                color="primary"
                onClick={async () => {
                  const url = file.url;
                  const fileName = `File-${file.file_id}`;
                  await downloadFile(url, fileName);
                }}
              >
                <FileDownloadOutlinedIcon />
              </IconButton>
              <div className="flex items-center gap-2">
                <ConfirmBox
                  // title="Confirm to submit"
                  message="Are you sure you want to delete this item?"
                  onConfirm={() => handleDelete(patientId, file.file_id)}
                >
                  <IconButton aria-label="delete" color="error">
                    <FaTrash />
                  </IconButton>
                </ConfirmBox>
              </div>
            </div>
            {selectedFileIndex === index && (
              <div
                className={`file-preview-expanded absolute z-10 w-1/2 bg-white shadow-lg rounded-lg p-4 flex items-center justify-center bottom-full mb-2`}
              >
                {file.file_type.includes('pdf') && (
                  <Document file={file.url} loading={<div>Loading PDF...</div>} error={<div>Failed to load PDF.</div>}>
                    <Page pageNumber={1} width={400} renderTextLayer={false} renderAnnotationLayer={false} />
                  </Document>
                )}
                {file.file_type.includes('image') && (
                  <img src={file.url} alt="file" style={{ maxWidth: '100%', height: 'auto' }} />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FileManager;